<template>
  <div class="page-my-account mt-5">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li>
          <router-link to="/dashboard">Dashboard</router-link>
        </li>
        <li class="is-active">
          <router-link to="/dashboard/my-account" aria-current="page">My account</router-link>
        </li>
      </ul>
    </nav>

    <div class="columns">
      <!-- Left Column: Profile Image -->
      <div class="column is-one-third left-column">
        <div class="profile-image-wrapper">
          <img :src="profileImageUrl" alt="User Image" class="user-image" />
          <!-- Edit button over the image -->
          <button class="edit-btn" @click="triggerFileInput">Edit</button>
          <input
            type="file"
            ref="fileInput"
            @change="onFileChange"
            accept="image/*"
            hidden
          />
        </div>
      </div>

      <!-- Right Column: Tabbed View -->
      <div class="column is-two-thirds">
        <div class="tabs is-boxed">
          <ul>
            <li
              :class="{ 'is-active': activeTab === 'details' }"
              @click="activeTab = 'details'"
            >
              <a>Details</a>
            </li>
            <li
              :class="{ 'is-active': activeTab === 'notifications' }"
              @click="activeTab = 'notifications'"
            >
              <a>Notification Preferences</a>
            </li>
          </ul>
        </div>
        <div class="tab-content">
          <div v-if="activeTab === 'details'" class="is-size-5">
            <p><strong>Username:</strong>  {{ userProfile.username }}</p>
            <p><strong>Email:</strong>  {{ userProfile.email }}</p>
            <p>
              <strong>Name:</strong>  {{ userProfile.first_name }} {{ userProfile.last_name }}
            </p>
            <p><strong>Access Type:</strong>  {{ userProfile.access_type }}</p>
            <p>
              <strong>Entities:</strong>
              {{ userProfile.sender_entities && userProfile.sender_entities.length 
                ? userProfile.sender_entities.map(entity => entity.name).join(', ') 
                : 'N/A' }}
            </p>
            <p>
              <strong>Roles:</strong>
              {{ userProfile.groups && userProfile.groups.length 
                ? userProfile.groups.map(group => group.name).join(', ') 
                : 'N/A' }}
            </p>
            <p>
              <strong>Department:</strong>
              {{ userProfile.store_code && userProfile.store_code.length 
                ? userProfile.store_code.map(dept => dept.code).join(', ') 
                : 'N/A' }}
            </p>
          </div>
          <div v-else-if="activeTab === 'notifications'">
            <div class="notification-settings">
              <h2 class="subtitle">Notification Preferences</h2>
              <div v-if="errorTypes.length">
                <div class="field" v-for="error in errorTypes" :key="error.key">
                  <label class="checkbox">
                    <input
                      type="checkbox"
                      v-model="selectedNotificationPreferences"
                      :value="error.key"
                      class="mr-2 is-vcentered"
                    />
                    <span>  {{ error.description }}</span>
                  </label>
                </div>
              </div>
              <div v-else>
                <p>Loading notification preferences...</p>
              </div>
              <button class="button is-primary mt-4" @click="saveNotificationPreferences">
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Buttons -->
    <div class="buttons">
      <button class="button is-danger" @click="logout">Log out</button>
      <!-- <button class="button is-primary" @click="resetPassword">Reset Password</button> -->
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toast } from 'bulma-toast';
import defaultProfileImage from '@/assets/default_profile.png';

export default {
  name: 'MyAccount',
  data() {
    return {
      userProfile: {
        username: '',
        access_type: '',
        first_name: '',
        last_name: '',
        emai: '',
        sender_entities: [],
        groups: [],
        store_code: [],
        notification_preferences: []
      },
      errorTypes: [],
      selectedNotificationPreferences: [],
      profileImageUrl: '',
      selectedImage: null,
      activeTab: 'details',
      defaultProfileImage
    };
  },
  async mounted() {
    await this.getUserProfile();
    await this.getErrorTypes();
  },
  methods: {
    async getUserProfile() {
      try {
        const response = await axios.get('/api/v1/user-profile/');
        this.userProfile = response.data;
        this.profileImageUrl =
          response.data.image && response.data.image.length
            ? response.data.image
            : this.defaultProfileImage;
        this.selectedNotificationPreferences = response.data.notification_preferences.map(
          (pref) => pref.key
        );
      } catch (error) {
        console.error(error);
      }
    },
    async getErrorTypes() {
      try {
        const response = await axios.get('/api/v1/user-profile/error_types/');
        this.errorTypes = response.data;
      } catch (error) {
        console.error('Error fetching notification types:', error);
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedImage = file;
        this.uploadProfileImage();
      }
    },
    async uploadProfileImage() {
      if (!this.selectedImage) return;
      const formData = new FormData();
      formData.append('image', this.selectedImage);
      try {
        const response = await axios.patch(
          '/api/v1/user-profile/upload-image/',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.profileImageUrl =
          response.data.image && response.data.image.length
            ? response.data.image
            : this.defaultProfileImage;
        toast({
          message: 'Profile image updated successfully!',
          type: 'is-success',
          dismissible: true,
          duration: 5000,
          position: 'top-right'
        });
      } catch (error) {
        console.error('Error uploading profile image:', error);
        toast({
          message: 'Failed to update profile image.',
          type: 'is-danger',
          dismissible: true,
          duration: 5000,
          position: 'top-right'
        });
      }
    },
    async saveNotificationPreferences() {
      try {
        const preferences = this.selectedNotificationPreferences.filter(
          (pref) => pref !== null
        );
        await axios.patch('/api/v1/user-profile/update-notifications/', {
          notification_preferences: preferences
        });
        toast({
          message: 'Notification preferences updated successfully!',
          type: 'is-success',
          dismissible: true,
          duration: 5000,
          position: 'top-right'
        });
      } catch (error) {
        console.error('Error saving notification preferences:', error);
        toast({
          message: 'Failed to update notification preferences.',
          type: 'is-danger',
          dismissible: true,
          duration: 5000,
          position: 'top-right'
        });
      }
    },
    async logout() {
      try {
        await axios.post('/api/v1/token/logout/');
        localStorage.removeItem('username');
        localStorage.removeItem('userid');
        localStorage.removeItem('token');
        axios.defaults.headers.common['Authorization'] = '';
        this.$store.commit('removeToken');
        this.$router.push('/');
      } catch (error) {
        console.error(error);
      }
    },
    async resetPassword() {
      try {
        await axios.post('/api/v1/users/reset_password/', {
          email: this.userProfile.email
        });
        toast({
          message: 'Password reset email sent. Please check your inbox.',
          type: 'is-success',
          dismissible: true,
          duration: 5000,
          position: 'top-right'
        });
      } catch (error) {
        console.error('Error resetting password:', error);
        toast({
          message: 'Error resetting password. Please try again.',
          type: 'is-danger',
          dismissible: true,
          duration: 5000,
          position: 'top-right'
        });
      }
    }
  }
};
</script>

<style scoped>
/* Left Column: make sure image container fills the available space and is centered */
.left-column {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.profile-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* Image fills the container */
.user-image {
  width: 90%;
  height: 90%;
  object-fit: cover;
  border-radius: 50%;
}

/* Edit button overlay on the image */
.edit-btn {
  position: absolute;
  bottom: 70px;
  left: 70px;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.edit-btn:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

/* Simple Bulma tabs styling */
.tabs {
  margin-bottom: 10px;
}

/* Fixed-size tab content */
.tab-content {
  border: 1px solid #dbdbdb;
  padding: 20px;
  height: 400px;
  overflow-y: auto;
  background: #fff;
}
</style>
